/**
 *
 * CommunicationForm
 *
 */
import React, {
  lazy,
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import "./style.scss";

import { Form } from "antd";

// components
import DateField from "components/Forms/Fields/DateField";
import HiddenField from "components/Forms/Fields/HiddenField";
import DynamicMultipleSelect from "components/Forms/Fields/DynamicMultipleSelect";
import RichEditorField from "components/Forms/Fields/RichEditorField";

//import ThumbnailUpload from "components/ThumbnailUpload";
const ThumbnailUpload = lazy(() => import("components/ThumbnailUpload"));

// helper for forms
import moment from "utils/moment/momentLocale";
import { generateAudienceIncludes } from "utils/helpers/helper.generateData";
// init forms
import { initFormsCommunication } from "utils/forms/forms.init";
// utils
import {
  prepearFormValue,
  prepearFormValueRemoveHiddenChain,
  setFormValues,
} from "utils/forms/forms.utils";

function CommunicationForm({
  mode,
  method,
  form,
  initialValues,
  name,
  dataSource,
  communicationDetail,
  disabeled,
  // actions
  handleSubmit,
  handleChange,
  handleValuesChange,
  // default
  className,
  ...rest
}) {
  const childClassNames = classNames("custom-form-wrapper", className);
  const { t, i18n } = useTranslation();

  const onlyRead = mode == "onlyRead";
  const isEdit = mode == "edit" || mode == "new";

  const getInitForm = useCallback(() => initFormsCommunication(), [i18n?.language]);

  useEffect(() => {
    if (!!form && !!communicationDetail) {
      const initForm = getInitForm();
      const itemObj = {
        ...communicationDetail,
        "description-editor": communicationDetail?.description || "",
      };
      setFormValues(form, initForm, itemObj);
    }
  }, []);

  const onFinish = values => {
    const sendingData = prepearFormValue(dataSource, values);
    const sendingDataClear = prepearFormValueRemoveHiddenChain(
      dataSource,
      sendingData,
    );
    handleSubmit(sendingDataClear);
  };

  const onFinishFailed = errorInfo => {
    console.error("Failed:", errorInfo);
  };

  const dataSourceHidden = [
    {
      label: "",
      name: "audience",
      required: true,
      value: communicationDetail?.audience || {},
      type: "hidden",
      col: 24,
    },
    {
      label: t("common.title"),
      name: "title",
      required: true,
      value: communicationDetail?.title || "",
      type: "hidden",
      col: 24,
    },
    {
      label: "",
      name: "description",
      required: true,
      value: "",
      type: "hidden",
      col: 24,
      placeholder: t("inputs.richEditorPlaceholderNews"),
    },
    {
      label: "",
      name: "status",
      required: true,
      value: "draft",
      type: "hidden",
      col: 24,
    },
    {
      label: "",
      name: "types",
      required: ["no-type"],
      value: null,
      type: "hidden",
      col: 24,
    },
  ];

  const audienseSelect = useMemo(() => {
    const onlyReadMode = mode == "onlyRead";
    return (
      <DynamicMultipleSelect
        key={`axios-select-multiple-communication`}
        index={`axios-select-multiple-communication`}
        mode={mode}
        method={method}
        onlyRead={onlyReadMode}
        form={form}
        data={{
          label: t("audiences.selectAudience"),
          name: "audience-select",
          endponit: `communications/audience/by/schools,students,courses,classes,audiences,users`,
          endponitSend: {
            value: "",
            include: generateAudienceIncludes(communicationDetail) || [],
            firstLoad: true,
          },
          displayLabel: true,
          required: true,
          type: "axios-select-multiple",
          hasModeView: true,
          hiddenChain: "audience",
          col: 24,
          method: "POST",
          groupped: true,
          optionLabelProp: "title", // 'label
          value: [],
          className: "axios-select",
          mode: "multiple",
          isSearch: true,
          placeholder: t("audiences.selectAudience"),
          showAsTag: true,
          onSearch: input => {},
        }}
        onChange={(values, options, init) => {
          const coreFormValues = form?.getFieldsValue();
          !!form &&
            onFormValuesChangeCallback(
              {
                audience: init,
                ["audience-select"]: values,
              },
              coreFormValues,
            );
        }}
      />
    );
  }, [
    form,
    mode,
    method,
    communicationDetail?._id,
    communicationDetail?.audience,
    communicationDetail?.["audience-select"],
    i18n?.language
  ]);

  const onFormValuesChangeCallback = useCallback(
    (changedValues, allValues) => {
      !!handleValuesChange && handleValuesChange(changedValues, allValues);
    },
    [form, mode, method, handleValuesChange],
  );

  const thumbnail = useMemo(() => {
    const onlyReadMode = mode == "onlyRead";
    const isEditable = mode == "edit" || mode == "new";
    return (
      <Suspense fallback="">
        <ThumbnailUpload
          name="thumbnail"
          visibility={
            isEditable || (!!communicationDetail?.thumbnail && onlyReadMode)
          }
          mode={mode}
          method={method}
          wrapForm={true}
          wrapperClassName="communication-body-header__upload"
          label={t("inputs.featuredImage")}
          onlyRead={onlyReadMode}
          value={communicationDetail?.thumbnail}
          onRemove={({ status }) => {
            if (status == 200) {
              form.setFieldsValue({
                thumbnail: null,
              });
              !!handleValuesChange &&
                handleValuesChange(
                  {
                    thumbnail: null,
                  },
                  form?.getFieldsValue(),
                );
            }
          }}
          onResult={(imageUrl) => {
            if (!!imageUrl) {
              form.setFieldsValue({
                thumbnail: imageUrl,
              });
              !!handleValuesChange &&
                handleValuesChange(
                  {
                    thumbnail: imageUrl,
                  },
                  form?.getFieldsValue(),
                );
            }
          }}
        />
      </Suspense>
    );
  }, [
    form,
    mode,
    method,
    communicationDetail?._id,
    communicationDetail?.thumbnail,
    handleValuesChange,
    i18n?.language,
    ThumbnailUpload,
  ]);

  return (
    <div className={childClassNames}>
      <Form
        form={form}
        initialValues={(!!initialValues && initialValues) || {}}
        name={name}
        className="custom-form"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onFieldsChange={handleChange}
        onValuesChange={onFormValuesChangeCallback}
        autoComplete="off"
      >
        <div className="communication-body-header">
          <div className="communication-body-header__fileds">
            {audienseSelect}

            {(isEdit || onlyRead) && (
              <DateField
                key={`date-communication`}
                data={{
                  label: t("inputs.deadlineForReply"),
                  onlyReadLabel: t("inputs.deadline"),
                  name: "deadlineDate",
                  required: false,
                  hasModeView: true,
                  value: null,
                  type: "date",
                  col: 24,
                }}
                index={`date-communication`}
                onlyRead={onlyRead}
                className={`${onlyRead ? " only-read" : ""}`}
                disabledDate={(current) => {
                  // Can not select days before today and today
                  return current && current < moment().endOf("day");
                }}
              />
            )}
          </div>
          {thumbnail}
        </div>

        <RichEditorField
          key={`rich-editor-communication`}
          mode={mode}
          form={form}
          data={{
            label: "",
            name: "description-editor",
            hiddenChain: "description",
            required: false,
            value: communicationDetail?.description || "",
            hasModeView: false,
            type: "rich-editor",
            type_editor: "simple",
            col: 24,
            placeholder: t("inputs.richEditorPlaceholderNews"),
            onChange: (text) => {
              //  console.log({ text });
            },
          }}
          index={`rich-editor-communication`}
          method={method}
        />
        {dataSourceHidden?.map(
          (item, index) =>
            item && (
              <HiddenField key={`hidden-${index}`} data={item} index={index} />
            ),
        )}
      </Form>
    </div>
  );
}

CommunicationForm.defaultProps = {
  disabeled: false,
  method: "update",
  name: "basic",
};

CommunicationForm.propTypes = {
  disabeled: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.string.isRequired,
  ]),
};

export default memo(CommunicationForm);
